import React from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Checkbox, FormControlLabel, Chip, IconButton
} from '@material-ui/core'
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons'
import { TASK_STATUS_LABELS } from '../../constants/bidStatuses'
import { TASK_TYPE_LABELS, PRIORITY_LABELS } from '../../constants/contextEnums'

const TaskFormModal = ({ open, onClose, onSave, task, projectId, url, users }) => {
  const isEdit = !!task
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [owner, setOwner] = React.useState('')
  const [assignees, setAssignees] = React.useState([])
  const [dueDate, setDueDate] = React.useState('')
  const [requiresReview, setRequiresReview] = React.useState(false)
  const [blockers, setBlockers] = React.useState([])
  const [blockerInput, setBlockerInput] = React.useState('')
  const [outputs, setOutputs] = React.useState([])
  const [outputInput, setOutputInput] = React.useState('')
  const [taskType, setTaskType] = React.useState('other')
  const [priority, setPriority] = React.useState('normal')
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (open) {
      if (task) {
        setTitle(task.title || '')
        setDescription(task.description || '')
        setOwner(task.owner?._id || task.owner || '')
        setAssignees((task.assignees || []).map(a => a._id || a))
        setDueDate(task.dueDate ? task.dueDate.slice(0, 10) : '')
        setRequiresReview(task.requiresReview || false)
        setBlockers(task.blockers || [])
        setOutputs(task.outputs || [])
        setTaskType(task.task_type || 'other')
        setPriority(task.priority || 'normal')
      } else {
        setTitle('')
        setDescription('')
        setOwner('')
        setAssignees([])
        setDueDate('')
        setRequiresReview(false)
        setBlockers([])
        setOutputs([])
        setTaskType('other')
        setPriority('normal')
      }
      setError('')
      setBlockerInput('')
      setOutputInput('')
    }
  }, [open, task])

  const handleSubmit = () => {
    if (!title.trim()) {
      setError('Title is required')
      return
    }
    const body = {
      title: title.trim(),
      description,
      owner: owner || undefined,
      assignees,
      dueDate: dueDate || null,
      requiresReview,
      blockers,
      outputs,
      task_type: taskType,
      priority
    }
    onSave(body, isEdit ? task._id : null)
  }

  const addBlocker = () => {
    if (blockerInput.trim()) {
      setBlockers([...blockers, blockerInput.trim()])
      setBlockerInput('')
    }
  }

  const addOutput = () => {
    if (outputInput.trim()) {
      setOutputs([...outputs, outputInput.trim()])
      setOutputInput('')
    }
  }

  const userList = users || []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Task' : 'New Task'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Title"
          fullWidth
          margin="dense"
          value={title}
          onChange={e => setTitle(e.target.value)}
          error={!!error}
          helperText={error}
          inputProps={{ maxLength: 300 }}
          autoFocus
        />
        <TextField
          label="Description"
          fullWidth
          margin="dense"
          multiline
          minRows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel>Task Type</InputLabel>
            <Select value={taskType} onChange={e => setTaskType(e.target.value)}>
              {Object.entries(TASK_TYPE_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Priority</InputLabel>
            <Select value={priority} onChange={e => setPriority(e.target.value)}>
              {Object.entries(PRIORITY_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <FormControl fullWidth margin="dense">
          <InputLabel>Owner</InputLabel>
          <Select value={owner} onChange={e => setOwner(e.target.value)}>
            {userList.map(u => (
              <MenuItem key={u._id} value={u._id}>{u.name || u.email || u._id}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel>Assignees</InputLabel>
          <Select
            multiple
            value={assignees}
            onChange={e => setAssignees(e.target.value)}
            renderValue={(selected) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selected.map(id => {
                  const user = userList.find(u => u._id === id)
                  return <Chip key={id} size="small" label={user?.name || id} />
                })}
              </div>
            )}
          >
            {userList.map(u => (
              <MenuItem key={u._id} value={u._id}>{u.name || u.email || u._id}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Due Date"
          type="date"
          fullWidth
          margin="dense"
          value={dueDate}
          onChange={e => setDueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={<Checkbox checked={requiresReview} onChange={e => setRequiresReview(e.target.checked)} />}
          label="Requires review before completion"
          style={{ marginTop: 8 }}
        />

        {/* Blockers */}
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <TextField label="Add blocker" size="small" value={blockerInput}
              onChange={e => setBlockerInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addBlocker())}
              style={{ flex: 1 }} />
            <IconButton size="small" onClick={addBlocker}><AddIcon fontSize="small" /></IconButton>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {blockers.map((b, i) => (
              <Chip key={i} size="small" label={b} onDelete={() => setBlockers(blockers.filter((_, j) => j !== i))} />
            ))}
          </div>
        </div>

        {/* Outputs */}
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <TextField label="Add output" size="small" value={outputInput}
              onChange={e => setOutputInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addOutput())}
              style={{ flex: 1 }} />
            <IconButton size="small" onClick={addOutput}><AddIcon fontSize="small" /></IconButton>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {outputs.map((o, i) => (
              <Chip key={i} size="small" label={o} onDelete={() => setOutputs(outputs.filter((_, j) => j !== i))} />
            ))}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TaskFormModal
